import { DataProvider } from "@shared/constants/common";
import { X_SP_UUID } from "@shared/env";
import UserInteractionLogger from "@shared/lib/analytics/internal/UserInteractionLogger";
import useAnalyticsHelpers from "@shared/modules/analytics/hooks/useAnalyticsHelpers";
import { SEATING_ITEM_TYPE_MAP } from "@shared/modules/seating/types/seating-property-maps";
import {
  MembershipGroupPropertyMap,
  SeatingEventPropertyMap,
} from "@shared/modules/seating/types/seating-property-types";
import { FormattedSeatType } from "@shared/modules/seating/types/seating-types";
import { useStore } from "@shared/store/useStore";
import { UseSeatingAnalyticsType } from "./useSeatingAnalytics.props";

export function useSeatingAnalytics(props: UseSeatingAnalyticsType) {
  const { trackItemsInCart } = useAnalyticsHelpers();
  const { seatingServiceState, seatingEventData } = props;

  const isMembershipPropertyMap = (
    eventPropertyMap: SeatingEventPropertyMap | undefined
  ): eventPropertyMap is MembershipGroupPropertyMap =>
    Boolean(eventPropertyMap?.membership_levels);

  // formats ticket/membership info for GTAG functions
  const getTrackingItem = (itemId: number) => {
    const item = seatingServiceState.itemTypeMap[itemId];
    return {
      itemId: item.id,
      itemName: seatingEventData?.name,
      itemVariant: item.name,
      itemType:
        SEATING_ITEM_TYPE_MAP[seatingServiceState.mapItemType].nestedItemType
          .name,
    };
  };

  const trackAddSeat = async (
    basketType: DataProvider,
    seats: Array<FormattedSeatType>
  ): Promise<void> => {
    const basket = useStore.getState().getBasket(basketType);
    const seatItemTypeId = seats.find(
      (seat) => seat.itemTypeId !== null && seat.itemTypeId !== undefined
    )?.itemTypeId;

    if (seatItemTypeId && basket) {
      const trackingItem = getTrackingItem(seatItemTypeId);
      const callbackFunc = async () => {
        await UserInteractionLogger.addToCart({
          item: {
            item_type: trackingItem.itemType,
            type: trackingItem.itemId,
          },
          count: seats.length,
          venue: seatingEventData?.venue?.name,
          event: isMembershipPropertyMap(seatingEventData)
            ? undefined
            : seatingEventData,
          membershipGroup: seatingEventData?.membership_levels
            ? {
                id: seatingEventData.id,
                membership_levels: seatingEventData.membership_levels,
              }
            : undefined,
          basket,
        });
      };
      await trackItemsInCart(
        seats.length,
        basket,
        trackingItem,
        seatingEventData?.venue,
        callbackFunc,
        { uuid: basket?.[X_SP_UUID] }
      );
    }
  };

  const trackModifyLocation = async (
    basketType: DataProvider,
    locationArray: Array<{
      itemTypeId: number;
      permissionId: number;
      qty: number;
    }>
  ): Promise<void> => {
    const basket = useStore.getState().getBasket(basketType);
    const totalQuantity = locationArray.reduce((a, b) => a + b.qty, 0);
    const locationItemTypeId = locationArray.find(
      (location) =>
        location.itemTypeId !== null && location.itemTypeId !== undefined
    )?.itemTypeId;
    if (locationItemTypeId && basket) {
      const trackingItem = getTrackingItem(locationItemTypeId);
      const callbackFunc = async () => {
        await UserInteractionLogger.addToCart({
          item: {
            item_type: trackingItem.itemType,
            type: trackingItem.itemId,
          },
          count: totalQuantity,
          venue: seatingEventData?.venue?.name,
          event: isMembershipPropertyMap(seatingEventData)
            ? undefined
            : seatingEventData,
          membershipGroup: seatingEventData?.membership_levels
            ? {
                id: seatingEventData.id,
                membership_levels: seatingEventData.membership_levels,
              }
            : undefined,
          basket,
        });
      };
      await trackItemsInCart(
        totalQuantity,
        basket,
        trackingItem,
        seatingEventData?.venue,
        callbackFunc,
        {
          uuid: basket?.[X_SP_UUID],
        }
      );
    }
  };

  return { trackModifyLocation, trackAddSeat };
}
